import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import type { ModelSubscription } from '@app/api/Subscriptions';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { formatAmount } from '@app/shared/utils/formatAmount';

import {
  SUBSCRIPTIONS_EMPTY_NAME,
  SUBSCRIPTIONS_UNLIMITED_PERIOD,
} from './constants';

import type { SubscriptionsColumnsHandlers } from '../types';
import type { ColumnsType } from 'antd/es/table';

const getServicesLabel = ({ Services }: ModelSubscription) =>
  Services?.length
    ? Services.map(({ Name, Count }) => `${Name ?? ''} × ${Count}`).join(', ')
    : '—';

const getPeriodLabel = ({ ValidFromPurchaseDays }: ModelSubscription) =>
  ValidFromPurchaseDays
    ? `${ValidFromPurchaseDays} дн.`
    : SUBSCRIPTIONS_UNLIMITED_PERIOD;

export const getSubscriptionsColumns = ({
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}: SubscriptionsColumnsHandlers): ColumnsType<ModelSubscription> => {
  const columns: ColumnsType<ModelSubscription> = [
    {
      title: generateSpanWithDataTestId('Название', 'subscription-name-title'),
      dataIndex: 'Name',
      key: 'Name',
      width: 240,
      render: (_, subscription) =>
        subscription.Name?.trim() || SUBSCRIPTIONS_EMPTY_NAME,
    },
    {
      title: generateSpanWithDataTestId(
        'Услуги',
        'subscription-services-title'
      ),
      key: 'services',
      width: 240,
      ellipsis: true,
      render: (_, subscription) => getServicesLabel(subscription),
    },
    {
      title: generateSpanWithDataTestId(
        'Срок действия',
        'subscription-period-title'
      ),
      key: 'period',
      width: 160,
      render: (_, subscription) => getPeriodLabel(subscription),
    },
    {
      title: generateSpanWithDataTestId(
        'Стоимость, ₽',
        'subscription-cost-title'
      ),
      dataIndex: 'SubscriptionCost',
      key: 'SubscriptionCost',
      width: 160,
      align: 'right',
      render: (cost: number) => formatAmount(cost),
    },
  ];

  if (!canEdit && !canDelete) {
    return columns;
  }

  columns.push({
    title: '',
    key: 'actions',
    width: 100,
    align: 'right',
    fixed: 'right',
    render: (_, subscription) => (
      <div className="Subscriptions__row-actions">
        {canEdit && (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(subscription)}
            data-testid="edit-subscription"
            aria-label="Редактировать абонемент"
          />
        )}
        {canDelete && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(subscription)}
            data-testid="delete-subscription"
            aria-label="Удалить абонемент"
          />
        )}
      </div>
    ),
  });

  return columns;
};
